import type { Edge, Node } from '@xyflow/react';
import { inferTypes, applyInferredTypesToNodesWithChange } from '@features/flows/lib/type-infer';
import { isTypeCompatible, isTypeCompatibleExpr, type TypeExpr } from '@features/flows/utils/types';
import type { CanvasState, SliceCreator } from '../types';
import { pushHistory } from '../utils';
import type { SliceCtx } from '../ctx';

type SliceKeys = 'inferAndApplyPinTypes' | 'isValidConnection';

export const createInferenceSlice: SliceCreator<Pick<CanvasState, SliceKeys>> = (set, get) => {
  const resolveCtx = (): SliceCtx | undefined => (get as any).__ctx;
  const ensureCtx = () => {
    const ctx = resolveCtx();
    if (!ctx) throw new Error('canvas ctx missing for inference slice');
    return ctx;
  };

  return {
    inferAndApplyPinTypes: () => {
      ensureCtx(); // creates parity with original structure; no direct ctx usage
      set((state) => {
        try {
          const inferred = inferTypes(state.graph.nodes, state.graph.edges, state.templates as any);
          const { nodes: nextNodes, changed } = applyInferredTypesToNodesWithChange(
            state.graph.nodes,
            inferred,
          );
          if (changed) {
            pushHistory(state);
            state.graph.nodes = nextNodes as any;
          }
        } catch {
          /* noop */
        }
      });
    },
    isValidConnection: (conn: any) => {
      ensureCtx();
      if (!conn?.sourceHandle || !conn?.targetHandle) return false;
      const nodes = get().graph.nodes;
      const templates = get().templates;
      const findPin = (node: Node | undefined, handle: string | null | undefined) => {
        if (!node || !handle) {
          return { kind: 'exec' as const, type: 'any' as const, typeExpr: undefined as TypeExpr | undefined };
        }
        const channel: 'data' | 'exec' = String(handle).startsWith('data:') ? 'data' : 'exec';
        const [, io, raw] = String(handle).split(':') as [string, 'in' | 'out', string];
        const pins = (node.data as any)?.pins;
        const arr = channel === 'data' ? pins?.data?.[io] : pins?.exec?.[io];
        const pin = Array.isArray(arr) ? arr.find((x: any) => x.id === raw) : undefined;
        let coarse = pin?.type as any;
        let texpr = (pin as any)?.typeExpr as TypeExpr | undefined;
        if (channel === 'data' && (coarse == null || coarse === 'any')) {
          const key = (node.data as any)?.templateKey || (node.data as any)?.handler || '';
          const tpl = key && templates ? templates.get(String(key)) : undefined;
          const fromTpl =
            tpl &&
            (io === 'in'
              ? tpl.ports.data.in.find((x) => x.id === raw)
              : tpl.ports.data.out.find((x) => x.id === raw));
          coarse = fromTpl?.type ?? coarse;
          texpr = texpr ?? fromTpl?.typeExpr;
        }
        if (coarse == null) coarse = 'any';
        return { kind: channel, type: coarse, typeExpr: texpr };
      };
      const src = nodes.find((node) => node.id === conn.source);
      const tgt = nodes.find((node) => node.id === conn.target);
      const s = findPin(src as any, conn.sourceHandle);
      const t = findPin(tgt as any, conn.targetHandle);
      if (s.kind !== t.kind) return false;
      if (s.kind === 'exec') return true;
      if (s.typeExpr && t.typeExpr) return isTypeCompatibleExpr(s.typeExpr, t.typeExpr);
      return isTypeCompatible(s.type as any, t.type as any);
    },
  };
};
